import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import React, { forwardRef, memo, SyntheticEvent, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	children?: React.ReactNode;
	align?: 'left' | 'right';
	style?: React.CSSProperties;
	onClose: () => unknown;
	closeOnScroll?: boolean;
}
const Dropdown = forwardRef<HTMLDivElement, Props>(
	({ className, children, onClose, style, closeOnScroll }, ref) => {
		const nodeRef = useRef<HTMLDivElement | null>(null);
		const onCloseRef = useRef(onClose);

		useEffect(() => {
			onCloseRef.current = onClose;
		}, [onClose]);

		const setRefs = useCallback(
			(node: HTMLDivElement | null) => {
				nodeRef.current = node;
				if (typeof ref === 'function') {
					ref(node);
				} else if (ref) {
					ref.current = node;
				}
			},
			[ref]
		);

		// Capture phase, so the click that opens a dropdown has already passed the document
		// before the listener exists. The close itself is deferred a tick so a trigger that
		// only ever opens still ends up closed when clicked while open.
		useEffect(() => {
			let closeTimer: ReturnType<typeof setTimeout> | undefined;
			const closeOnOutsideClick = (event: Event) => {
				const node = nodeRef.current;
				if (!node || !(event.target instanceof Node) || node.contains(event.target)) {
					return;
				}
				clearTimeout(closeTimer);
				closeTimer = setTimeout(() => onCloseRef.current(), 0);
			};

			document.addEventListener('click', closeOnOutsideClick, true);

			return () => {
				clearTimeout(closeTimer);
				document.removeEventListener('click', closeOnOutsideClick, true);
			};
		}, []);

		useEffect(() => {
			if (!closeOnScroll) {
				return;
			}

			const closeOnAncestorScroll = (event: Event) => {
				const node = nodeRef.current;
				if (!node || !(event.target instanceof Node) || !event.target.contains(node)) {
					return;
				}
				onCloseRef.current();
			};

			document.addEventListener('scroll', closeOnAncestorScroll, true);
			return () => {
				document.removeEventListener('scroll', closeOnAncestorScroll, true);
			};
		}, [closeOnScroll]);

		const onClickInsideBox = useCallback((event: SyntheticEvent) => {
			event.stopPropagation();
		}, []);

		return (
			<div ref={setRefs} onClick={onClickInsideBox} className={className} style={style}>
				{children}
			</div>
		);
	}
);

export const DefaultDropdownMenu = styled.div`
	width: 100%;
	display: flex;
	border-radius: 8px;
	flex-direction: column;
	box-sizing: border-box;
	background-color: ${({ theme }) => theme.v2.color.neutral[300]};
	overflow: auto;

	${({ theme }) => theme.shadow.z1}
`;

export const DefaultDropdownEntry = styled(
	({
		className,
		active,
		children,
		disabled,
		...props
	}: {
		className?: string;
		active?: boolean;
		children?: React.ReactNode;
		disabled?: boolean;
		[key: string]: unknown;
	}) => {
		return (
			<div className={className} {...props}>
				{active ? <Icon size="lg" variant="checkmark-circle" /> : null}
				<Text disabled={disabled} elipsis variant="button">
					{children}
				</Text>
			</div>
		);
	}
)`
	display: flex;
	padding: 0.7rem 1rem;
	cursor: pointer;

	& > ${Icon} {
		margin-right: 1rem;
	}
	&:hover {
		background-color: ${({ theme }) => theme.v2.color.secondary[200]};
	}
`;

const Memoized = memo(Dropdown);
const Styled = styled(Memoized)`
	position: absolute;
	z-index: 10;

	${Text} {
		min-width: 4rem;
	}

	${({ align }) => {
		switch (align) {
			case 'left':
				return `
            left: 0;
        `;
			default:
				return `
            right: 0;
        `;
		}
	}};
`;

export { Styled as Dropdown };
